import { getHubItems } from './hubs';

/**
 * Rail-mode flyout popup. Anchored to the hub icon when the user hovers
 * over a collapsed sidebar tile. Reuses the same items structure (flat
 * or grouped) as HubRow. Extracted from AdminLayout.jsx during the
 * iter175 layout refactor.
 */
export function Flyout({
  hub,
  anchorRect,
  onClose,
  onKeepOpen,
  isActiveFn,
  getBadgeCount,
  permissions,
  onItemClick,
}) {
  const Icon = hub.hubIcon;
  const allItems = getHubItems(hub);
  const visibleItems = allItems.filter(i => !permissions || !i.permission || permissions[i.permission]);

  // Reusable single-item button used in both grouped and flat rendering.
  const renderButton = (item) => {
    const isActv = isActiveFn(item.href);
    const ItemIcon = item.icon;
    const badgeCount = item.badge ? getBadgeCount(item.badge) : null;
    return (
      <button key={item.href} onClick={() => onItemClick(item.href)} data-testid={`admin-flyout-${item.testId}`}
        style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          padding: '8px 10px', borderRadius: '7px', border: 'none',
          background: isActv ? 'var(--hs-sidebar-active)' : 'transparent',
          color: isActv ? 'var(--hs-sidebar-text)' : 'var(--hs-sidebar-text-muted)',
          fontSize: '13px', fontWeight: isActv ? 700 : 500,
          cursor: 'pointer', textAlign: 'left', width: '100%',
          transition: 'background 0.12s ease',
        }}
        onMouseEnter={(e) => { if (!isActv) e.currentTarget.style.background = 'var(--hs-sidebar-hover)'; }}
        onMouseLeave={(e) => { if (!isActv) e.currentTarget.style.background = 'transparent'; }}>
        <ItemIcon size={14} strokeWidth={isActv ? 2.2 : 1.7} />
        <span style={{ flex: 1 }}>{item.label}</span>
        {badgeCount !== null && (
          <span style={{ fontSize: '10px', fontWeight: 700, padding: '1px 6px', borderRadius: '999px', background: 'var(--hs-sidebar-hover)', color: 'var(--hs-sidebar-text)' }}>{badgeCount}</span>
        )}
      </button>
    );
  };

  return (
    <div
      onMouseEnter={onKeepOpen}
      onMouseLeave={onClose}
      data-testid={`admin-hub-flyout-${hub.id}`}
      style={{
        position: 'fixed',
        top: Math.max(8, anchorRect.top - 4),
        left: anchorRect.left + 8,
        minWidth: '240px',
        background: 'var(--hs-sidebar)',
        border: '1px solid var(--hs-sidebar-border)',
        borderRadius: '10px',
        padding: '12px',
        boxShadow: '0 14px 40px rgba(15,23,42,0.4)',
        zIndex: 1000,
        animation: 'flyoutIn 0.12s ease-out',
      }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid var(--hs-sidebar-border)', color: 'var(--hs-sidebar-text)' }}>
        <Icon size={20} />
        <span style={{ fontSize: '13px', fontWeight: 800 }}>{hub.label}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {hub.groups ? (
          hub.groups.map((g) => {
            const groupItems = g.items.filter(i => !permissions || !i.permission || permissions[i.permission]);
            if (groupItems.length === 0) return null;
            return (
              <div key={g.label} style={{ marginTop: 4 }}>
                <div style={{
                  fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em',
                  color: 'var(--hs-sidebar-text-muted)', opacity: 0.7,
                  padding: '6px 10px 3px', userSelect: 'none',
                }}>{g.label}</div>
                {groupItems.map(renderButton)}
              </div>
            );
          })
        ) : (
          visibleItems.map(renderButton)
        )}
      </div>
      <style>{`@keyframes flyoutIn { from { opacity: 0; transform: translateX(-4px); } to { opacity: 1; transform: translateX(0); } }`}</style>
    </div>
  );
}
